import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CalendarDays, MapPin, Mic, Ticket, CheckCircle2, Presentation, Utensils } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { posterUrl, seminarWhen, priceLabel } from "@/lib/seminars";
import { publicApi, PublicShell, SeatsInfo } from "@/pages/public/PublicSeminars";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", participants: 1, participant_names: [], church: "", notes: "", lunches: 0, website: "" };

function BookingForm({ s, onDone }) {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setCount = (n) => {
    const c = Math.max(1, Math.min(50, n || 1));
    setF({ ...f, participants: c, participant_names: Array.from({ length: c - 1 }, (_, i) => f.participant_names[i] || "") });
  };
  const max = s.seats_left ?? 50;
  const mut = useMutation({
    mutationFn: async () => (await publicApi.post(`/seminars/${s.id}/bookings`, f)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["public-seminar", s.id] }); onDone({ ...d, email: f.email }); },
    onError: (e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Controlla i dati inseriti"),
  });
  const valid = f.first_name.trim() && f.last_name.trim() && /\S+@\S+\.\S+/.test(f.email) && f.phone.trim().length >= 5 && f.participants <= max;
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (valid) mut.mutate(); }} className="space-y-3" data-testid="booking-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Nome *</Label><Input value={f.first_name} onChange={set("first_name")} className="mt-1 bg-white" data-testid="booking-first-name"/></div>
        <div><Label className="text-xs">Cognome *</Label><Input value={f.last_name} onChange={set("last_name")} className="mt-1 bg-white" data-testid="booking-last-name"/></div>
        <div><Label className="text-xs">Email *</Label><Input type="email" value={f.email} onChange={set("email")} className="mt-1 bg-white" data-testid="booking-email"/></div>
        <div><Label className="text-xs">Telefono *</Label><Input type="tel" value={f.phone} onChange={set("phone")} className="mt-1 bg-white" data-testid="booking-phone"/></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Numero di persone *</Label><Input type="number" min="1" max={max} value={f.participants} onChange={(e) => setCount(parseInt(e.target.value, 10))} className="mt-1 bg-white" data-testid="booking-participants"/></div>
        <div><Label className="text-xs">Chiesa o ente di provenienza</Label><Input value={f.church} onChange={set("church")} className="mt-1 bg-white" data-testid="booking-church"/></div>
      </div>
      {f.participant_names.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs">Nomi degli altri partecipanti</Label>
          {f.participant_names.map((n, i) => (
            <Input key={i} value={n} placeholder={`Partecipante ${i + 2}`} className="bg-white" data-testid={`booking-participant-${i}`}
              onChange={(e) => setF({ ...f, participant_names: f.participant_names.map((x, j) => (j === i ? e.target.value : x)) })}/>
          ))}
        </div>
      )}
      <div><Label className="text-xs">Note</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} className="mt-1 bg-white" data-testid="booking-notes"/></div>
      <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true"/>
      {s.lunch_price > 0 && (
        <div className="p-3 rounded-lg bg-[#F5F1EA] border border-[#1C352D]/10">
          <Label className="text-xs">Pranzi da prenotare (€ {Number(s.lunch_price).toFixed(2)} cad.)</Label>
          <Input type="number" min="0" value={f.lunches} onChange={(e) => setF({ ...f, lunches: Math.max(0, parseInt(e.target.value, 10) || 0) })} className="mt-1 bg-white w-32" data-testid="booking-lunches"/>
          <div className="text-[11px] text-stone-500 mt-1">Numero totale di pranzi (es. 2 persone × 2 giorni = 4).</div>
        </div>
      )}
      {(s.external_price > 0 || f.lunches > 0) && <div className="text-sm">Totale: <b data-testid="booking-total">€ {((s.external_price || 0) * f.participants + (s.lunch_price || 0) * f.lunches).toFixed(2)}</b></div>}
      {f.participants > max && <div className="text-xs text-red-700">Restano solo {max} posti.</div>}
      <Button type="submit" disabled={!valid || mut.isPending} className="w-full h-11 rounded-full bg-[#E2725B] hover:bg-[#C95F49] text-white" data-testid="booking-submit">
        {mut.isPending ? "Invio…" : "Prenota"}
      </Button>
    </form>
  );
}

function Done({ d, instructions }) {
  return (
    <div className="p-6 rounded-2xl bg-white border border-emerald-200 text-center" data-testid="booking-success">
      <CheckCircle2 size={36} className="mx-auto text-emerald-600"/>
      <div className="font-semibold text-lg mt-2">Prenotazione ricevuta</div>
      <p className="text-sm text-stone-600 mt-1">Ti abbiamo inviato un riepilogo a <b>{d.email}</b>. Ti ricontatteremo per la conferma.</p>
      {d.total_amount > 0 && instructions && <div className="mt-4 text-left text-sm whitespace-pre-line p-3 rounded-lg bg-[#F5F1EA]"><b>Come pagare (€ {d.total_amount.toFixed(2)})</b>{"\n"}{instructions}</div>}
    </div>
  );
}

export default function PublicSeminarDetail() {
  const { id } = useParams();
  const [done, setDone] = useState(null);
  const { data: s, isLoading, error } = useQuery({ queryKey: ["public-seminar", id], queryFn: async () => (await publicApi.get(`/seminars/${id}`)).data });
  const { data: cfg } = useQuery({ queryKey: ["public-seminar-config"], queryFn: async () => (await publicApi.get("/seminars/config")).data });
  if (isLoading) return <PublicShell><div className="text-stone-500 text-sm">Caricamento…</div></PublicShell>;
  if (error || !s) return <PublicShell><div className="text-stone-500">Seminario non disponibile. <Link to="/seminari" className="underline">Torna al catalogo</Link></div></PublicShell>;
  const soldOut = s.seats_left === 0;
  return (
    <PublicShell>
      <Link to="/seminari" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-6"><ArrowLeft size={13}/> Tutti i seminari</Link>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-10">
        <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#1C352D]/5 border border-[#1C352D]/10 grid place-items-center">
          {s.has_poster ? <img src={posterUrl(s)} alt={s.name} className="w-full h-full object-cover" data-testid="public-seminar-poster"/> : <Presentation size={48} className="text-[#1C352D]/30"/>}
        </div>
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="public-seminar-title">{s.name}</h1>
          <div className="mt-4 text-sm text-stone-700 space-y-1.5">
            <div className="flex items-center gap-2"><CalendarDays size={15}/>{seminarWhen(s)}</div>
            {s.location && <div className="flex items-center gap-2"><MapPin size={15}/>{s.location}</div>}
            {s.speaker && <div className="flex items-center gap-2"><Mic size={15}/>{s.speaker}</div>}
            <div className="flex items-center gap-2" data-testid="public-seminar-price"><Ticket size={15}/>{priceLabel(s.external_price)} {s.seats_left != null && <>· <SeatsInfo s={s}/></>}</div>
            {s.lunch_price > 0 && <div className="flex items-center gap-2" data-testid="public-seminar-lunch"><Utensils size={15}/>Pranzo prenotabile · € {Number(s.lunch_price).toFixed(2)}</div>}
          </div>
          {s.description && <p className="mt-5 text-stone-700 whitespace-pre-line leading-relaxed">{s.description}</p>}
          <div className="mt-8 p-5 rounded-2xl bg-white/70 border border-[#1C352D]/10">
            {done ? <Done d={done} instructions={cfg?.payment_instructions}/> : soldOut ? (
              <div className="text-center text-red-700 font-medium py-6" data-testid="public-seminar-soldout">Posti esauriti</div>
            ) : (
              <>
                <div className="font-semibold mb-3">Prenota il tuo posto</div>
                <BookingForm s={s} onDone={setDone}/>
              </>
            )}
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
